import React, { useEffect, useState } from 'react'
import {
  Grid,
  Typography,
  FormControlLabel,
  RadioGroup,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { FormattedMessage } from 'react-intl'
import { useRouter } from '@src/routes'
import { useFormikContext } from 'formik'
import { MilliplexFormValues } from '../../Milliplex'
import Analytes from '../Analytes'
import { InstrumentTypeItem } from '@src/types/graphql-types'
import LiquidCheckbox from '@src/components/LiquidCheckbox'
import ValidationErrors from '@src/routes/Configurators/validations/ValidationErrors'
import HandleMarkup from '@src/components/HandleMarkup'
import StyledRadio from '@src/routes/Configurators/utils/ConfiguratorCustomRadioButton'
import { PlateTypeWithStatus } from '@src/routes/Configurators/products/Milliplex/utils'

const useStyles = makeStyles((theme: Theme) => ({
  part2HeaderStyle: {
    marginTop: theme.spacing(6),
  },
  analytesHeaderStyle: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(4),
  },
  instrumentTypesContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  instrumentTypeLabel: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  checkboxContainer: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  plateTypeControl: {
    marginRight: theme.spacing(8),
  },
  plateTypeRadio: {
    paddingRight: theme.spacing(0),
    '& .MuiIconButton-label': {
      marginRight: theme.spacing(0.5),
    },
  },
}))

export interface AdvancedSearchCriteriaProps {
  displayPageError: boolean
  setDisplayPageError: React.Dispatch<React.SetStateAction<boolean>>
  plateType: PlateTypeWithStatus[]
  instrumentType: InstrumentTypeItem[]
}

const AdvancedSearchCriteria: React.FC<AdvancedSearchCriteriaProps> = ({
  displayPageError,
  setDisplayPageError,
  plateType,
  instrumentType,
}) => {
  const classes = useStyles()
  const anyLuminex = 'Any Luminex® Instrumentation'
  const { query, replace, pathname } = useRouter()
  const [enabledInstrumentTypes, setEnabledInstrumentTypes] = useState<
    string[]
  >([])
  const formik = useFormikContext<MilliplexFormValues>()
  const instrumentTypeList = [
    ...new Set(instrumentType.flatMap((single) => single.instrumentType)),
  ]

  useEffect(() => {
    if (formik.values.plateType && instrumentType) {
      setEnabledInstrumentTypes(
        instrumentType.find(
          (instr) => instr.plateType === formik.values.plateType
        )?.instrumentType ?? []
      )
    }
  }, [instrumentType, formik.values.plateType])

  const plateTypeChange = (e) => {
    const value = e.target.value
    const enabledInstrument =
      instrumentType.find((instr) => instr.plateType === value)
        ?.instrumentType ?? []
    formik.setFieldTouched('analytes', false)
    if (formik.values.instrumentType.length > 0) {
      const filteredValues =
        query.anyInstrument === 'true'
          ? enabledInstrument
          : formik.values.instrumentType.filter((instrument) =>
              enabledInstrument.includes(instrument)
            )
      formik.setValues({
        ...formik.values,
        plateType: value,
        instrumentType: filteredValues,
        analytes: [],
      })
      replace(
        {
          pathname: pathname,
          query: {
            ...query,
            plateType: value,
            instrumentType: filteredValues,
            analytes: [],
          },
        },
        undefined,
        { shallow: true }
      )
    } else {
      formik.setValues({
        ...formik.values,
        plateType: value,
        analytes: [],
      })
      replace(
        {
          pathname: pathname,
          query: { ...query, plateType: value, analytes: [] },
        },
        undefined,
        { shallow: true }
      )
    }
  }
  const instrumentTypeChange = (e) => {
    setDisplayPageError(false)
    formik.setFieldTouched('analytes', false)
    const checked = e.target.checked,
      value = e.target.value
    let filteredValues: string[] = []
    let anyInstrument = query.anyInstrument || 'false'
    if (value === anyLuminex) {
      filteredValues = enabledInstrumentTypes
      if (checked) {
        anyInstrument = 'true'
      } else {
        anyInstrument = 'false'
      }
    } else {
      filteredValues = formik.values.instrumentType.filter(
        (instrument) => instrument !== value
      )
      checked && filteredValues.push(value)
    }

    formik.setValues({
      ...formik.values,
      instrumentType: filteredValues,
      analytes: [],
    })
    replace(
      {
        pathname: pathname,
        query: {
          ...query,
          instrumentType: filteredValues,
          analytes: [],
          anyInstrument: anyInstrument,
        },
      },
      undefined,
      { shallow: true }
    )
  }
  const formatLabel = (label: string): string => {
    return label.replaceAll('®', '<sup>®</sup>')
  }
  const checkPlateTypeDisabled = (plate: PlateTypeWithStatus) => {
    const species = query.species
    const researchAreas = query.researchAreas
    if (
      !plate.enabled ||
      !!(query.previouslyViewed && plate.plateType !== formik.values.plateType)
    ) {
      return true
    } else if (plate.plateType === '384-well') {
      //TODO: This is temporary fix for handling the logic of disabling the 384 well.
      return !(
        species === 'Human' &&
        (!researchAreas || researchAreas.includes('Immunology/Inflammation'))
      )
    } else return false
  }
  const instrumentRadioTypeChange = (e) => {
    setDisplayPageError(false)
    formik.setFieldTouched('analytes', false)
    const filteredValues = [e.target.value]
    formik.setValues({
      ...formik.values,
      instrumentType: filteredValues,
      analytes: [],
    })
    replace(
      {
        pathname: pathname,
        query: { ...query, instrumentType: filteredValues, analytes: [] },
      },
      undefined,
      { shallow: true }
    )
  }
  const instrumentCheckedStatus = (instrument: string): boolean => {
    if (instrument === anyLuminex) {
      return query.anyInstrument === 'true'
    } else {
      return formik.values.instrumentType.some(
        (single) => single === instrument
      )
    }
  }

  return (
    <>
      <Grid container>
        <Grid item xs={12} className={classes.part2HeaderStyle}>
          <Typography variant="h2">
            <FormattedMessage
              id="ADVANCED_SEARCH_CRITERIA_UPDATED"
              defaultMessage="advanced search criteria"
            />
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h3" className={classes.part2HeaderStyle}>
            <FormattedMessage id="PLATE_TYPE" defaultMessage="Plate Type" />
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            className={classes.analytesHeaderStyle}
          >
            <FormattedMessage
              id="MILLIPLEX_SELECT_PLATE_TYPE"
              defaultMessage="Select Plate Type"
            />
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <RadioGroup
            row
            aria-label="brand-selection"
            onChange={plateTypeChange}
            value={formik.values.plateType}
          >
            {plateType.map((plate) => (
              <FormControlLabel
                key={plate.plateType}
                className={classes.plateTypeControl}
                aria-label="brand"
                value={plate.plateType}
                control={
                  <StyledRadio
                    isConfiguratorsRedesign="true"
                    className={classes.plateTypeRadio}
                  />
                }
                label={plate.plateType}
                disabled={checkPlateTypeDisabled(plate)}
              />
            ))}
          </RadioGroup>
        </Grid>
        <ValidationErrors
          errors={
            formik.touched.plateType && formik.errors.plateType
              ? Array.isArray(formik.errors.plateType)
                ? formik.errors.plateType
                : [formik.errors.plateType]
              : []
          }
          configuratorsRedesign
        />
        <Grid item xs={12}>
          <Typography variant="h3" className={classes.part2HeaderStyle}>
            <FormattedMessage
              id="INSTRUMENT_PLATFORM"
              defaultMessage="Instrument Platform"
            />
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography
            variant="subtitle2"
            className={classes.analytesHeaderStyle}
          >
            {formik.values.assay === 'Qualified' ? (
              <FormattedMessage
                id="MILLIPLEX_SELECT_INSTRUMENTS"
                defaultMessage="Select Instrument(s)"
              />
            ) : (
              <FormattedMessage
                id="MILLIPLEX_SELECT_INSTRUMENT"
                defaultMessage="Select Instrument"
              />
            )}
          </Typography>
        </Grid>

        {formik.values.assay === 'Qualified' && (
          <Grid container className={classes.checkboxContainer}>
            {[anyLuminex]
              .concat(instrumentTypeList.sort())
              .map((instrument) => {
                const disabled =
                  (instrument !== anyLuminex &&
                    (!enabledInstrumentTypes.includes(instrument) ||
                      query.anyInstrument === 'true')) ||
                  (instrument === anyLuminex && formik.values.plateType === '')
                return (
                  <Grid item key={instrument}>
                    <LiquidCheckbox
                      aria-label={instrument}
                      value={instrument}
                      checked={instrumentCheckedStatus(instrument)}
                      onChange={instrumentTypeChange}
                      label={
                        <HandleMarkup
                          value={formatLabel(instrument)}
                          className={classes.instrumentTypeLabel}
                        />
                      }
                      disabled={disabled}
                    />
                  </Grid>
                )
              })}
          </Grid>
        )}

        {(formik.values.assay === 'Screening' ||
          formik.values.assay === 'Both Qualified and Screening') && (
          <Grid
            item
            xs={12}
            direction="row"
            className={classes.instrumentTypesContainer}
          >
            <RadioGroup
              row
              aria-label="brand-selection"
              onChange={instrumentRadioTypeChange}
              value={formik.values.instrumentType[0] || null}
            >
              {instrumentTypeList.sort().map((instrument) => {
                const disabled = !enabledInstrumentTypes.includes(instrument)
                return (
                  <FormControlLabel
                    key={instrument}
                    aria-label="brand"
                    value={instrument}
                    control={<StyledRadio isConfiguratorsRedesign="true" />}
                    label={
                      <HandleMarkup
                        value={formatLabel(instrument)}
                        className={classes.instrumentTypeLabel}
                      />
                    }
                    disabled={disabled}
                  />
                )
              })}
            </RadioGroup>
          </Grid>
        )}

        <ValidationErrors
          errors={
            formik.touched.instrumentType && formik.errors.instrumentType
              ? Array.isArray(formik.errors.instrumentType)
                ? formik.errors.instrumentType
                : [formik.errors.instrumentType]
              : []
          }
          configuratorsRedesign
        />
        <Grid item xs={12}>
          <Analytes
            displayPageError={displayPageError}
            setDisplayPageError={setDisplayPageError}
          />
        </Grid>
      </Grid>
    </>
  )
}

export default AdvancedSearchCriteria
